import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MasterViewModel } from '../../../base-classes/master-view-model';
import { HttpClient } from '@angular/common/http';
import { DataPreparation } from '../models/data-preparation.model';
import { ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { DataPreparationService } from '../services/data-preparation.service';
import { ToastService } from '../../../toast/toast.service';
import { MESSAGE_CONFIG } from '../../../config/menu-config';
import { environment } from '../../../../environments/environment';

@Injectable()
export class DataPreparationViewModel extends MasterViewModel<DataPreparation> {
  dataPreparationService: DataPreparationService;
  
  // Dialog state
  uploadDialog: boolean = false;
  isEditMode: boolean = false;
  currentFile: DataPreparation | null = null;
  
  // Dropdown options
  clients: any[] = [];
  packages: any[] = [];
  
  // Selected values
  selectedClient: any = null;
  selectedPackage: any = null;
  
  // File upload
  selectedFile: File | null = null;
  uploadProgress: number = 0;
  isUploading: boolean = false;
  
  // Table data
  uploadedFiles: DataPreparation[] = [];

  constructor(
    http: HttpClient,
    private messageService: ToastService,
    public confirmationService: ConfirmationService,
    private router: Router
  ) {
    const baseUrl = `${environment.apiUrl}/data-preparation`;
    const moduleName = 'Data Preparation';
    super(http, baseUrl, moduleName);
    this.dataPreparationService = new DataPreparationService(http);
  }

  /**
   * Initialize data
   */
  initialize(): void {
    this.loadClients();
    this.loadUploadedFiles();
  }

  /**
   * Load user's accessible clients from API
   */
  loadClients(): void {
    this.dataPreparationService.getClients().subscribe({
      next: (response: any) => {
        const clientData = response.data || response;
        this.clients = clientData;
      },
      error: (error) => {
        this.messageService.error('Failed to load clients', MESSAGE_CONFIG.ERROR);
        console.error('Error loading clients:', error);
      }
    });
  }

  /**
   * Handle client selection change
   */
  onClientChange(): void {
    this.selectedPackage = null;
    this.packages = [];
    
    if (this.selectedClient && this.selectedClient.id) {
      this.loadPackagesByClient(this.selectedClient.id);
    }
  }

  /**
   * Load packages by selected client
   */
  loadPackagesByClient(clientId: string, callback?: (packages: any[]) => void): void {
    this.dataPreparationService.getPackagesByClient(clientId).subscribe({
      next: (response: any) => {
        const packagesData = response.data || response;
        this.packages = packagesData;
        if (callback) {
          callback(packagesData);
        }
      },
      error: (error) => {
        this.messageService.error('Failed to load packages', MESSAGE_CONFIG.ERROR);
        console.error('Error loading packages:', error);
      }
    });
  }

  /**
   * Handle file selection
   */
  onFileSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      // Check file extension (more reliable than MIME type)
      const fileName = file.name.toLowerCase();
      const allowedExtensions = ['.xlsx', '.xls', '.xlsm'];
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!hasValidExtension) {
        this.messageService.error('Only Excel files (.xlsx, .xls, .xlsm) are allowed', MESSAGE_CONFIG.ERROR);
        return;
      }

      // Check file size (50MB = 52,428,800 bytes)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        this.messageService.error(`File size exceeds 50MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`, MESSAGE_CONFIG.ERROR);
        return;
      }
      
      this.selectedFile = file;
      this.messageService.success(`File "${file.name}" selected successfully`, MESSAGE_CONFIG.SUCCESS);
    }
  }

  /**
   * Show upload dialog
   */
  showUploadDialog(): void {
    this.uploadDialog = true;
    this.isEditMode = false;
    this.currentFile = null;
    this.selectedClient = null;
    this.selectedPackage = null;
    this.selectedFile = null;
    this.uploadProgress = 0;
  }

  /**
   * Show edit dialog with pre-filled data
   */
  editFile(file: DataPreparation): void {
    this.isEditMode = true;
    this.currentFile = file;
    this.uploadDialog = true;
    this.selectedFile = null;
    this.uploadProgress = 0;

    // Pre-fill client
    const client = this.clients.find(c => c.id === file.clientPackageMapping?.client?.id);
    if (client) {
      this.selectedClient = client;
      if (file.clientPackageMapping) {

        const packageMapping = {
          id: file.clientPackageMapping.id,
          clientId: file.clientPackageMapping.client.id,
          packageId: file.clientPackageMapping.package.id,
          package: file.clientPackageMapping.package
        };
        

        this.packages = [packageMapping];
        this.selectedPackage = packageMapping;
        
      }
    }
  }

  /**
   * Hide upload dialog
   */
  hideUploadDialog(): void {
    this.uploadDialog = false;
    this.isEditMode = false;
    this.currentFile = null;
    this.selectedClient = null;
    this.selectedPackage = null;
    this.selectedFile = null;
    this.uploadProgress = 0;
  }

  /**
   * Upload file to S3 (create or update)
   */
  uploadFile(): void {
    if (!this.selectedClient) {
      this.messageService.error('Please select a client first', MESSAGE_CONFIG.ERROR);
      return;
    }

    if (!this.selectedPackage) {
      this.messageService.error('Please select a package first', MESSAGE_CONFIG.ERROR);
      return;
    }

    if (!this.isEditMode && !this.selectedFile) {
      this.messageService.error('Please select a file to upload', MESSAGE_CONFIG.ERROR);
      return;
    }

    this.isUploading = true;

    // Edit mode: update existing file
    if (this.isEditMode && this.currentFile) {
      this.handleUpdate();
      return;
    }

    // Create mode: upload new file
    this.handleCreate();
  }

  private handleCreate(): void {
    this.dataPreparationService.uploadFile(
      this.selectedClient.id,
      this.selectedPackage.packageId,
      this.selectedFile!
    ).subscribe({
      next: () => {
        this.isUploading = false;
        this.messageService.success('File uploaded successfully', MESSAGE_CONFIG.SUCCESS);
        this.hideUploadDialog();
        this.loadUploadedFiles();
      },
      error: (error) => {
        this.isUploading = false;
        const errorMessage = error.error?.message || error.message || 'Failed to upload file';
        this.messageService.error(errorMessage, MESSAGE_CONFIG.ERROR);
        console.error('Upload error:', error);
      }
    });
  }

  private handleUpdate(): void {
    // Update with new file
    if (this.selectedFile) {
      this.dataPreparationService.replaceFile(
        this.currentFile!.id,
        this.selectedClient.id,
        this.selectedPackage.packageId,
        this.selectedFile
      ).subscribe({
        next: () => {
          this.isUploading = false;
          this.messageService.success('File updated successfully', MESSAGE_CONFIG.SUCCESS);
          this.hideUploadDialog();
          this.loadUploadedFiles();
        },
        error: (error) => {
          this.isUploading = false;
          const errorMessage = error.error?.message || error.message || 'Failed to update file';
          this.messageService.error(errorMessage, MESSAGE_CONFIG.ERROR);
          console.error('Update error:', error);
        }
      });
      return;
    }

    // Update metadata only (client/package mapping)
    const updateData = {
      clientPackageId: this.selectedPackage.id
    };
    
    this.dataPreparationService.updateTestData(this.currentFile!.id, updateData).subscribe({
      next: () => {
        this.isUploading = false;
        this.messageService.success('File updated successfully', MESSAGE_CONFIG.SUCCESS);
        this.hideUploadDialog();
        this.loadUploadedFiles();
      },
      error: (error) => {
        this.isUploading = false;
        const errorMessage = error.error?.message || error.message || 'Failed to update file';
        this.messageService.error(errorMessage, MESSAGE_CONFIG.ERROR);
        console.error('Update error:', error);
      }
    });
  }

  /**
   * Load uploaded files
   */
  loadUploadedFiles(): void {
    this.dataPreparationService.getAllTestData().subscribe({
      next: (files: DataPreparation[]) => {
        this.uploadedFiles = files;
      },
      error: (error) => {
        this.messageService.error('Failed to load uploaded files', MESSAGE_CONFIG.ERROR);
        console.error('Error loading files:', error);
      }
    });
  }

  /**
   * Delete file (soft delete)
   */
  deleteFile(item: DataPreparation): void {
    if (!item.id) return;
    
    this.confirmationService.confirm({
      message: `${MESSAGE_CONFIG.DELETE_CONFIRM_TEXT}this file?`,
      header: MESSAGE_CONFIG.CONFIRM,
      accept: () => {
        this.dataPreparationService.deleteTestData(item.id).subscribe({
          next: (result) => {
            this.messageService.success(result.message || 'File deleted successfully', MESSAGE_CONFIG.SUCCESS);
            this.loadUploadedFiles();
          },
          error: (error) => {
            const errorMessage = error.error?.message || error.message || 'Failed to delete file';
            this.messageService.error(errorMessage, MESSAGE_CONFIG.ERROR);
            console.error('Delete error:', error);
          }
        });
      }
    });
  }

  /**
   * Download file
   */
  downloadFile(file: DataPreparation): void {
    if (!file.id) return;

    this.dataPreparationService.downloadFile(file.id).subscribe({
      next: (blob: Blob) => {
        // Create a temporary URL for the blob
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary anchor element and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = file.filename || 'download.xlsx';
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.messageService.success('File downloaded successfully', MESSAGE_CONFIG.SUCCESS);
      },
      error: (error) => {
        const errorMessage = error.error?.message || error.message || 'Failed to download file';
        this.messageService.error(errorMessage, MESSAGE_CONFIG.ERROR);
        console.error('Download error:', error);
      }
    });
  }

  /**
   * Global filter for table
   */
  onGlobalFilter(table: Table, event: Event): void {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }
}
